import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeft, ArrowRight, TriangleAlert } from 'lucide-react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    EvidenceFamilyBadge,
    RunProvenanceCard,
    shortSha,
    SOURCE_LABELS,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import {
    useVoiceEvalBaselineComparison,
    useVoiceEvalRunComparison,
    useVoiceEvalRuns,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type EvalBaselineComparisonEntry,
    type EvalCaseResultListItem,
    type EvalRunComparisonEntry,
    type EvalRunListItem,
} from '@libraries/typescript/api_client';

const HISTORICAL_BASELINE_VALUE = 'historical';
const BASELINE_WINDOW_OPTIONS = [7, 14, 30] as const;

const baselineWindowSearchSchema = z
    .preprocess(
        (value) => (value === undefined ? undefined : Number(value)),
        z.union([z.literal(7), z.literal(14), z.literal(30)]),
    )
    .optional()
    .default(7)
    .catch(7);

export const Route = createFileRoute('/_app/admin/voice-evals/compare')({
    head: () => ({
        meta: [{ title: 'Compare Eval Runs | Phoebe Admin' }],
    }),
    component: VoiceEvalComparePage,
    validateSearch: z.object({
        base: z.string().optional(),
        candidate: z.string().optional(),
        window_days: baselineWindowSearchSchema,
    }),
});

const VERDICT_ORDER: ReadonlyArray<EvalRunComparisonEntry['verdict']> = [
    'newly_failing',
    'flakiness_changed',
    'newly_passing',
    'added',
    'removed',
    'still_failing',
    'still_passing',
];

const VERDICT_LABELS: Record<EvalRunComparisonEntry['verdict'], string> = {
    newly_failing: 'Newly failing',
    flakiness_changed: 'Flakiness changed',
    newly_passing: 'Newly passing',
    added: 'Added',
    removed: 'Removed',
    still_failing: 'Still failing',
    still_passing: 'Still passing',
};

const VERDICT_BADGE_CLASSES: Record<EvalRunComparisonEntry['verdict'], string> =
    {
        newly_failing: 'bg-red-100 text-red-800 hover:bg-red-100',
        flakiness_changed: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
        newly_passing: 'bg-green-100 text-green-800 hover:bg-green-100',
        added: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
        removed: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
        still_failing: 'bg-red-50 text-red-700 hover:bg-red-50',
        still_passing: 'bg-muted text-muted-foreground hover:bg-muted',
    };

const BASELINE_VERDICT_ORDER: ReadonlyArray<
    EvalBaselineComparisonEntry['verdict']
> = [
    'newly_failing',
    'failing_flaky_baseline',
    'still_failing',
    'missing_from_candidate',
    'improved',
    'no_baseline',
    'still_passing',
];

const BASELINE_VERDICT_LABELS: Record<
    EvalBaselineComparisonEntry['verdict'],
    string
> = {
    newly_failing: 'Newly failing',
    failing_flaky_baseline: 'Failing flaky baseline',
    still_failing: 'Still failing',
    missing_from_candidate: 'Missing from candidate',
    improved: 'Improved',
    no_baseline: 'No baseline',
    still_passing: 'Still passing',
};

const BASELINE_VERDICT_BADGE_CLASSES: Record<
    EvalBaselineComparisonEntry['verdict'],
    string
> = {
    newly_failing: 'bg-red-100 text-red-800 hover:bg-red-100',
    failing_flaky_baseline: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    still_failing: 'bg-red-50 text-red-700 hover:bg-red-50',
    missing_from_candidate: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    improved: 'bg-green-100 text-green-800 hover:bg-green-100',
    no_baseline: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    still_passing: 'bg-muted text-muted-foreground hover:bg-muted',
};

function runOptionLabel(run: EvalRunListItem): string {
    const finished = run.finished_at ?? run.created_at;
    const dirty = run.git_is_dirty ? ' · dirty' : '';
    return [
        format(new Date(finished), 'MMM d h:mm a'),
        SOURCE_LABELS[run.source],
        `${run.git_branch ?? 'unknown'}@${shortSha(run.git_commit) ?? '?'}${dirty}`,
        run.suite_selector ?? '',
    ]
        .filter(Boolean)
        .join(' · ');
}

function SideOutcome({
    result,
}: {
    readonly result: EvalCaseResultListItem | null | undefined;
}) {
    if (!result) {
        return <span className="text-muted-foreground text-xs">—</span>;
    }
    if (result.flaky) {
        return (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                FLAKY {result.passed}/{result.attempted}
            </Badge>
        );
    }
    if (result.success) {
        return (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                PASS
            </Badge>
        );
    }
    return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            FAIL {result.passed}/{result.attempted}
        </Badge>
    );
}

function BaselineSummaryCard({
    baselineRunCount,
    windowDays,
}: {
    readonly baselineRunCount: number;
    readonly windowDays: number;
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                    Historical baseline
                </CardTitle>
            </CardHeader>
            <CardContent>
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
                    <dt className="text-muted-foreground text-sm">Runs</dt>
                    <dd className="text-sm">
                        {baselineRunCount.toLocaleString()} over {windowDays}{' '}
                        days
                    </dd>
                    <dt className="text-muted-foreground text-sm">Source</dt>
                    <dd className="text-sm">{SOURCE_LABELS.ci_daily}</dd>
                    <dt className="text-muted-foreground text-sm">
                        Clean only
                    </dt>
                    <dd className="text-sm">Yes</dd>
                </dl>
            </CardContent>
        </Card>
    );
}

function formatBaselinePassShare(value: number): string {
    return `${Math.round(value * 100)}%`;
}

function BaselineCell({
    baseline,
}: {
    readonly baseline: EvalBaselineComparisonEntry['baseline'];
}) {
    if (!baseline) {
        return <span className="text-muted-foreground text-xs">—</span>;
    }
    return (
        <span className="text-sm whitespace-nowrap">
            {formatBaselinePassShare(baseline.pass_share)} of{' '}
            {baseline.results.toLocaleString()}
            {baseline.flaky > 0 &&
                ` · ${baseline.flaky.toLocaleString()} flaky`}
        </span>
    );
}

function RunSelect({
    label,
    value,
    runs,
    includeHistorical,
    onChange,
}: {
    readonly label: string;
    readonly value: string | undefined;
    readonly runs: Array<EvalRunListItem>;
    readonly includeHistorical?: boolean;
    readonly onChange: (runId: string) => void;
}) {
    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-muted-foreground text-xs font-medium">
                {label}
            </span>
            <Select value={value ?? ''} onValueChange={onChange}>
                <SelectTrigger className="h-8">
                    <SelectValue placeholder={`Select ${label}…`} />
                </SelectTrigger>
                <SelectContent>
                    {includeHistorical === true && (
                        <SelectItem value={HISTORICAL_BASELINE_VALUE}>
                            Historical average (clean CI daily)
                        </SelectItem>
                    )}
                    {runs.map((run) => (
                        <SelectItem key={run.id} value={run.id}>
                            {runOptionLabel(run)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function VoiceEvalComparePage() {
    const router = useRouter();
    const search = Route.useSearch();
    const { data: runsData } = useVoiceEvalRuns({ per_page: 100 });
    const isHistoricalMode = search.base === HISTORICAL_BASELINE_VALUE;

    const {
        data: runComparison,
        isLoading: runComparisonIsLoading,
        error: runComparisonError,
    } = useVoiceEvalRunComparison(
        isHistoricalMode ? null : (search.base ?? null),
        search.candidate ?? null,
    );
    const {
        data: baselineComparison,
        isLoading: baselineComparisonIsLoading,
        error: baselineComparisonError,
    } = useVoiceEvalBaselineComparison(
        isHistoricalMode ? (search.candidate ?? null) : null,
        { window_days: search.window_days },
    );

    // The dropdowns list recent runs; a deep-linked comparison can
    // reference older runs, so fold the compared runs into the options
    // to keep the pickers labeled for any URL.
    const recentRuns = runsData?.items ?? [];
    const recentRunIds = new Set(recentRuns.map((run) => run.id));
    const runs = [
        ...recentRuns,
        ...[
            runComparison?.base,
            runComparison?.candidate,
            baselineComparison?.candidate,
        ].filter(
            (run): run is EvalRunListItem =>
                run !== undefined && !recentRunIds.has(run.id),
        ),
    ];

    const setSide = (side: 'base' | 'candidate') => (runId: string) => {
        void router.navigate({
            to: '/admin/voice-evals/compare',
            search: {
                ...search,
                [side]: runId,
                window_days:
                    side === 'base' && runId !== HISTORICAL_BASELINE_VALUE
                        ? undefined
                        : search.window_days,
            },
        });
    };

    // Historical mode compares a candidate against clean CI-daily runs,
    // so only the candidate can carry uncommitted changes.
    const dirtySides = isHistoricalMode
        ? [baselineComparison?.candidate.git_is_dirty ? 'candidate' : null]
        : [
              runComparison?.base.git_is_dirty ? 'base' : null,
              runComparison?.candidate.git_is_dirty ? 'candidate' : null,
          ];
    const dirtySideLabels = dirtySides.filter(Boolean);
    const dirtyWarning = dirtySideLabels.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <TriangleAlert className="h-4 w-4 shrink-0" />
            The {dirtySideLabels.join(' and ')}{' '}
            {dirtySideLabels.length > 1 ? 'runs have' : 'run has'} uncommitted
            changes — this is not an apples-to-apples comparison against
            committed code.
        </div>
    );
    const familyMismatch =
        runComparison !== undefined &&
        runComparison.base.family !== runComparison.candidate.family;

    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/admin/voice-evals">
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        All runs
                    </Link>
                </Button>
            </div>
            <div>
                <h1 className="text-lg font-semibold">Compare runs</h1>
                <p className="text-muted-foreground text-sm">
                    Case-level diff between a baseline and a candidate run,
                    joined on case identity.
                </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <RunSelect
                    label="Baseline"
                    value={search.base}
                    runs={runs}
                    includeHistorical={true}
                    onChange={setSide('base')}
                />
                <ArrowRight className="text-muted-foreground mb-2 h-4 w-4 shrink-0" />
                <RunSelect
                    label="Candidate"
                    value={search.candidate}
                    runs={runs}
                    includeHistorical={false}
                    onChange={setSide('candidate')}
                />
                {isHistoricalMode && (
                    <div className="flex min-w-32 flex-col gap-1">
                        <span className="text-muted-foreground text-xs font-medium">
                            Window
                        </span>
                        <Select
                            value={String(search.window_days)}
                            onValueChange={(value) =>
                                void router.navigate({
                                    to: '/admin/voice-evals/compare',
                                    search: {
                                        ...search,
                                        window_days: Number(value),
                                    },
                                })
                            }
                        >
                            <SelectTrigger className="h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {BASELINE_WINDOW_OPTIONS.map((windowDays) => (
                                    <SelectItem
                                        key={windowDays}
                                        value={String(windowDays)}
                                    >
                                        {windowDays} days
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
            {!search.base || !search.candidate ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Pick a baseline and a candidate run to diff their case
                    results.
                </div>
            ) : isHistoricalMode ? (
                baselineComparisonError ? (
                    <div className="text-muted-foreground py-12 text-center text-sm">
                        Historical comparison unavailable — the selected run was
                        not found. Check the run id in the URL or pick a run
                        above.
                    </div>
                ) : baselineComparisonIsLoading || !baselineComparison ? (
                    <div className="text-muted-foreground py-12 text-center text-sm">
                        Loading historical comparison…
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {dirtyWarning}
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-muted-foreground">
                                Candidate evidence:
                            </span>
                            <EvidenceFamilyBadge
                                family={baselineComparison.candidate.family}
                            />
                            <span className="text-muted-foreground text-xs">
                                Confirm the historical baseline uses the same
                                harness before interpreting latency.
                            </span>
                        </div>
                        <div className="grid gap-3 lg:grid-cols-2">
                            <BaselineSummaryCard
                                baselineRunCount={
                                    baselineComparison.baseline_run_count
                                }
                                windowDays={baselineComparison.window_days}
                            />
                            <RunProvenanceCard
                                run={baselineComparison.candidate}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {BASELINE_VERDICT_ORDER.filter(
                                (verdict) =>
                                    (baselineComparison.verdict_counts[
                                        verdict
                                    ] ?? 0) > 0,
                            ).map((verdict) => (
                                <Badge
                                    key={verdict}
                                    className={
                                        BASELINE_VERDICT_BADGE_CLASSES[verdict]
                                    }
                                >
                                    {baselineComparison.verdict_counts[verdict]}{' '}
                                    {BASELINE_VERDICT_LABELS[
                                        verdict
                                    ].toLowerCase()}
                                </Badge>
                            ))}
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Verdict</TableHead>
                                    <TableHead>Case</TableHead>
                                    <TableHead>Use case</TableHead>
                                    <TableHead>Baseline</TableHead>
                                    <TableHead>Candidate</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {baselineComparison.entries.map((entry) => (
                                    <TableRow key={entry.registry_id}>
                                        <TableCell>
                                            <Badge
                                                className={
                                                    BASELINE_VERDICT_BADGE_CLASSES[
                                                        entry.verdict
                                                    ]
                                                }
                                            >
                                                {
                                                    BASELINE_VERDICT_LABELS[
                                                        entry.verdict
                                                    ]
                                                }
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                to="/admin/voice-evals/cases"
                                                search={{
                                                    registry_id:
                                                        entry.registry_id,
                                                }}
                                                className="font-mono text-xs underline-offset-2 hover:underline"
                                            >
                                                {entry.case_name}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-xs">
                                            {entry.use_case ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            <BaselineCell
                                                baseline={entry.baseline}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {entry.candidate ? (
                                                <Link
                                                    to="/admin/voice-evals/runs/$runId/cases/$caseResultId"
                                                    params={{
                                                        runId: baselineComparison
                                                            .candidate.id,
                                                        caseResultId:
                                                            entry.candidate.id,
                                                    }}
                                                >
                                                    <SideOutcome
                                                        result={entry.candidate}
                                                    />
                                                </Link>
                                            ) : (
                                                <SideOutcome
                                                    result={entry.candidate}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )
            ) : runComparisonError ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Comparison unavailable — one of the selected runs was not
                    found. Check the run ids in the URL or pick runs above.
                </div>
            ) : runComparisonIsLoading || !runComparison ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Loading comparison…
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {dirtyWarning}
                    {familyMismatch && (
                        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <TriangleAlert className="h-4 w-4 shrink-0" />
                            These runs have different evidence families. Case
                            diffs can still be useful, but pipeline, service
                            health, and latency claims are not comparable.
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-muted-foreground">Baseline:</span>
                        <EvidenceFamilyBadge
                            family={runComparison.base.family}
                        />
                        <span className="text-muted-foreground">
                            Candidate:
                        </span>
                        <EvidenceFamilyBadge
                            family={runComparison.candidate.family}
                        />
                    </div>
                    <div className="grid gap-3 lg:grid-cols-2">
                        <RunProvenanceCard run={runComparison.base} />
                        <RunProvenanceCard run={runComparison.candidate} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {VERDICT_ORDER.filter(
                            (verdict) =>
                                (runComparison.verdict_counts[verdict] ?? 0) >
                                0,
                        ).map((verdict) => (
                            <Badge
                                key={verdict}
                                className={VERDICT_BADGE_CLASSES[verdict]}
                            >
                                {runComparison.verdict_counts[verdict]}{' '}
                                {VERDICT_LABELS[verdict].toLowerCase()}
                            </Badge>
                        ))}
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Verdict</TableHead>
                                <TableHead>Case</TableHead>
                                <TableHead>Use case</TableHead>
                                <TableHead>Baseline</TableHead>
                                <TableHead>Candidate</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {runComparison.entries.map((entry) => (
                                <TableRow key={entry.registry_id}>
                                    <TableCell>
                                        <Badge
                                            className={
                                                VERDICT_BADGE_CLASSES[
                                                    entry.verdict
                                                ]
                                            }
                                        >
                                            {VERDICT_LABELS[entry.verdict]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            to="/admin/voice-evals/cases"
                                            search={{
                                                registry_id: entry.registry_id,
                                            }}
                                            className="font-mono text-xs underline-offset-2 hover:underline"
                                        >
                                            {entry.case_name}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {entry.use_case ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {entry.base ? (
                                            <Link
                                                to="/admin/voice-evals/runs/$runId/cases/$caseResultId"
                                                params={{
                                                    runId: runComparison.base
                                                        .id,
                                                    caseResultId: entry.base.id,
                                                }}
                                            >
                                                <SideOutcome
                                                    result={entry.base}
                                                />
                                            </Link>
                                        ) : (
                                            <SideOutcome result={entry.base} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {entry.candidate ? (
                                            <Link
                                                to="/admin/voice-evals/runs/$runId/cases/$caseResultId"
                                                params={{
                                                    runId: runComparison
                                                        .candidate.id,
                                                    caseResultId:
                                                        entry.candidate.id,
                                                }}
                                            >
                                                <SideOutcome
                                                    result={entry.candidate}
                                                />
                                            </Link>
                                        ) : (
                                            <SideOutcome
                                                result={entry.candidate}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {entry.base && entry.candidate && (
                                            <Link
                                                to="/admin/voice-evals/compare-case"
                                                search={{
                                                    base: runComparison.base.id,
                                                    candidate:
                                                        runComparison.candidate
                                                            .id,
                                                    registry_id:
                                                        entry.registry_id,
                                                }}
                                                className="text-xs underline-offset-2 hover:underline"
                                            >
                                                Diff
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
